import React, { useCallback, useEffect, useState, Component } from 'react';
import { Search, Eye, X, ChevronLeft, ChevronRight, Loader2, AlertCircle, CheckCircle, AlertTriangle } from 'lucide-react';
import { useAuth } from '../store/authStore';
import { getAllAlerts, resolveAlert } from '../api/alerts';
// --- Types ---
interface Alert {
  id: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  alertType: string;
  message: string;
  status: 'active' | 'resolved';
  triggeredAt: string;
  resolvedAt?: string;
  user?: {
    id: string;
    fullName: string;
    email: string;
  };
  device?: {
    id: string;
    deviceUid: string;
  };
  sessionId?: string;
}
// --- Main Component ---
export function Alerts() {
  const {
    token
  } = useAuth();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [totalAlerts, setTotalAlerts] = useState(0);
  const [activeAlertsCount, setActiveAlertsCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  // Filters & Pagination
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  // Modals & Panels State
  const [viewAlert, setViewAlert] = useState<Alert | null>(null);
  const [isResolveAllOpen, setIsResolveAllOpen] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);
  // Fetch Data
  const fetchAlerts = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const filters = {
        search: debouncedSearch,
        status: statusFilter === 'all' ? undefined : statusFilter,
        severity: severityFilter === 'all' ? undefined : severityFilter,
        page,
        limit
      };
      const res = await getAllAlerts(token, filters);
      setAlerts(res.alerts || []);
      setTotalAlerts(res.total || 0);
      // Fetch true active count from a separate query (not page-bound)
      try {
        const activeRes = await getAllAlerts(token, {
          status: 'active',
          limit: 1
        });
        setActiveAlertsCount(activeRes.total || 0);
      } catch {
        setActiveAlertsCount(0);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load alerts');
    } finally {
      setIsLoading(false);
    }
  }, [token, debouncedSearch, statusFilter, severityFilter, page, limit]);
  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);
  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({
      message,
      type
    });
    setTimeout(() => setToast(null), 3000);
  };
  const handleResolve = async (id: string) => {
    if (!token) return;
    try {
      await resolveAlert(token, id);
      showToast('Alert resolved');
      fetchAlerts();
    } catch (err: any) {
      showToast(err.message || 'Failed to resolve alert', 'error');
    }
  };
  const handleResolveAll = async () => {
    if (!token) return;
    try {
      // In a real app, call a bulk resolve endpoint
      const activeIds = alerts.filter(a => a.status === 'active').map(a => a.id);
      await Promise.all(activeIds.map(id => resolveAlert(token, id)));
      showToast('All active alerts resolved');
      setIsResolveAllOpen(false);
      fetchAlerts();
    } catch (err: any) {
      showToast(err.message || 'Failed to resolve alerts', 'error');
    }
  };
  const getRelativeTime = (timestamp: string) => {
    const diffMs = new Date().getTime() - new Date(timestamp).getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hr ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };
  const formatAlertType = (type: string) => {
    return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
  };
  return <div className="relative min-h-full pb-20" data-id="element-53">
      {/* Header */}
      <div className="flex justify-between items-center mb-6" data-id="element-54">
        <div data-id="element-55">
          <h1 className="text-[24px] font-semibold text-white" data-id="element-56">Alerts</h1>
          <p className="text-[14px] mt-1" data-id="element-57">
            {isLoading ? <span className="text-text-secondary" data-id="element-58">Loading...</span> : activeAlertsCount > 0 ? <span className="text-danger font-medium" data-id="element-59">
                {activeAlertsCount} active alerts
              </span> : <span className="text-success font-medium" data-id="element-60">All clear</span>}
          </p>
        </div>
        {activeAlertsCount > 0 && <button onClick={() => setIsResolveAllOpen(true)} className="h-[36px] px-4 bg-transparent border border-danger text-danger font-semibold rounded-btn hover:bg-[rgba(239,68,68,0.1)] transition-colors" data-id="element-61">
            Resolve All
          </button>}
      </div>

      {/* Filters Bar */}
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap" data-id="element-62">
        <div className="flex items-center gap-3 flex-wrap" data-id="element-63">
          <div className="relative" data-id="element-64">
            <Search className="w-4 h-4 text-text-tertiary absolute left-3 top-1/2 -translate-y-1/2" data-id="element-65" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search message or device..." className="w-[240px] h-[36px] bg-surface border border-border rounded-input pl-9 pr-4 text-[14px] text-white placeholder:text-text-tertiary focus:outline-none focus:border-accent transition-colors" data-id="element-66" />
          </div>

          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="h-[36px] bg-surface border border-border rounded-input px-3 text-[14px] text-white focus:outline-none focus:border-accent appearance-none pr-8 relative" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%238A8A8A' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 8px center'
        }} data-id="element-67">
            <option value="all" data-id="element-68">All Status</option>
            <option value="active" data-id="element-69">Active</option>
            <option value="resolved" data-id="element-70">Resolved</option>
          </select>

          <select value={severityFilter} onChange={e => setSeverityFilter(e.target.value)} className="h-[36px] bg-surface border border-border rounded-input px-3 text-[14px] text-white focus:outline-none focus:border-accent appearance-none pr-8 relative" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%238A8A8A' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 8px center'
        }} data-id="element-71">
            <option value="all" data-id="element-72">All Severity</option>
            <option value="critical" data-id="element-73">Critical</option>
            <option value="high" data-id="element-74">High</option>
            <option value="medium" data-id="element-75">Medium</option>
            <option value="low" data-id="element-76">Low</option>
          </select>
        </div>

        <div className="text-[13px] text-text-secondary" data-id="element-77">
          Showing {alerts.length > 0 ? (page - 1) * limit + 1 : 0}-
          {Math.min(page * limit, totalAlerts)} of {totalAlerts}
        </div>
      </div>

      {/* Table */}
      <div className="bg-surface border border-border rounded-card overflow-hidden" data-id="element-78">
        <div className="overflow-x-auto" data-id="element-79">
          <table className="w-full text-left border-collapse" data-id="element-80">
            <thead data-id="element-81">
              <tr className="bg-surface-elevated border-b border-border h-[44px]" data-id="element-82">
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[90px]" data-id="element-83">
                  Severity
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[160px]" data-id="element-84">
                  Type
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px]" data-id="element-85">
                  Message
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[180px]" data-id="element-86">
                  User
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[120px]" data-id="element-87">
                  Device
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[140px]" data-id="element-88">
                  Triggered
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[100px]" data-id="element-89">
                  Status
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[100px]" data-id="element-90">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody data-id="element-91">
              {isLoading ? <tr data-id="element-92">
                  <td colSpan={8} className="h-[200px] text-center text-text-secondary" data-id="element-93">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" data-id="element-94" />
                    Loading alerts...
                  </td>
                </tr> : alerts.length === 0 ? <tr data-id="element-95">
                  <td colSpan={8} className="h-[200px] text-center text-text-secondary" data-id="element-96">
                    No alerts found matching your criteria.
                  </td>
                </tr> : alerts.map(alert => {
              let severityColor = '';
              let severityBg = '';
              let severityBorder = '';
              if (alert.severity === 'critical' || alert.severity === 'high') {
                severityColor = 'text-danger';
                severityBg = 'bg-[rgba(239,68,68,0.15)]';
                severityBorder = 'border-danger';
              } else if (alert.severity === 'medium') {
                severityColor = 'text-warning';
                severityBg = 'bg-[rgba(245,158,11,0.15)]';
                severityBorder = 'border-warning';
              } else {
                severityColor = 'text-success';
                severityBg = 'bg-[rgba(34,197,94,0.15)]';
                severityBorder = 'border-success';
              }
              return <tr key={alert.id} className="h-[56px] border-b border-[#1E1E1E] hover:bg-[#1E1E1E] transition-colors relative" data-id="element-97">
                      {/* Left border indicator */}
                      <td className="absolute left-0 top-0 bottom-0 w-1" style={{
                  backgroundColor: alert.severity === 'critical' || alert.severity === 'high' ? '#EF4444' : alert.severity === 'medium' ? '#F59E0B' : '#22C55E'
                }} data-id="element-98" />

                      <td className="px-4 pl-6" data-id="element-99">
                        <span className={`px-2 py-0.5 rounded-badge ${severityBg} ${severityColor} text-[12px] border border-transparent`} data-id="element-100">
                          {alert.severity.charAt(0).toUpperCase() + alert.severity.slice(1)}
                        </span>
                      </td>
                      <td className="px-4 text-[14px] font-medium text-white" data-id="element-101">
                        {formatAlertType(alert.alertType)}
                      </td>
                      <td className="px-4" data-id="element-102">
                        <p className="text-[13px] text-text-secondary truncate max-w-[300px]" title={alert.message} data-id="element-103">
                          {alert.message}
                        </p>
                      </td>
                      <td className="px-4" data-id="element-104">
                        {alert.user ? <div className="flex flex-col" data-id="element-105">
                            <span className="text-[13px] text-white truncate w-[160px]" data-id="element-106">
                              {alert.user.fullName}
                            </span>
                            <span className="text-[11px] text-text-secondary truncate w-[160px]" data-id="element-107">
                              {alert.user.email}
                            </span>
                          </div> : <span className="text-[13px] text-text-tertiary" data-id="element-108">
                            —
                          </span>}
                      </td>
                      <td className="px-4" data-id="element-109">
                        {alert.device ? <span className="text-[13px] font-mono text-white" data-id="element-110">
                            {alert.device.deviceUid}
                          </span> : <span className="text-[13px] text-text-tertiary" data-id="element-111">
                            —
                          </span>}
                      </td>
                      <td className="px-4" data-id="element-112">
                        <span className="text-[13px] text-text-secondary" title={new Date(alert.triggeredAt).toLocaleString()} data-id="element-113">
                          {getRelativeTime(alert.triggeredAt)}
                        </span>
                      </td>
                      <td className="px-4" data-id="element-114">
                        {alert.status === 'active' ? <span className="px-2 py-0.5 rounded-badge bg-[rgba(239,68,68,0.1)] text-danger text-[12px] border border-[rgba(239,68,68,0.2)]" data-id="element-115">
                            Active
                          </span> : <span className="px-2 py-0.5 rounded-badge bg-[rgba(34,197,94,0.1)] text-success text-[12px] border border-[rgba(34,197,94,0.2)]" data-id="element-116">
                            Resolved
                          </span>}
                      </td>
                      <td className="px-4" data-id="element-117">
                        <div className="flex items-center gap-1" data-id="element-118">
                          <button onClick={() => setViewAlert(alert)} className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-border transition-colors text-text-tertiary hover:text-white" data-id="element-119">
                            <Eye className="w-4 h-4" data-id="element-120" />
                          </button>
                          {alert.status === 'active' && <div className="relative group" data-id="element-121">
                              <button onClick={() => handleResolve(alert.id)} className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-[rgba(34,197,94,0.1)] transition-colors text-success" data-id="element-122">
                                <CheckCircle className="w-4 h-4" data-id="element-123" />
                              </button>
                              {/* Simple tooltip */}
                              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-surface-elevated border border-border rounded text-[11px] text-white whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all" data-id="element-124">
                                Resolve Alert
                              </div>
                            </div>}
                        </div>
                      </td>
                    </tr>;
            })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4" data-id="element-125">
        <div className="flex items-center gap-2" data-id="element-126">
          <select value={limit} onChange={e => {
          setLimit(Number(e.target.value));
          setPage(1);
        }} className="h-[32px] bg-surface border border-border rounded-btn px-2 text-[13px] text-white focus:outline-none focus:border-accent" data-id="element-127">
            <option value={25} data-id="element-128">25 per page</option>
            <option value={50} data-id="element-129">50 per page</option>
            <option value={100} data-id="element-130">100 per page</option>
          </select>
        </div>

        <div className="flex items-center gap-2" data-id="element-131">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="h-[32px] px-3 bg-surface border border-border rounded-btn text-[13px] text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-border transition-colors flex items-center gap-1" data-id="element-132">
            <ChevronLeft className="w-4 h-4" data-id="element-133" /> Prev
          </button>

          <button className="w-[32px] h-[32px] bg-accent text-background rounded-btn text-[13px] font-medium flex items-center justify-center" data-id="element-134">
            {page}
          </button>

          <button onClick={() => setPage(p => p + 1)} disabled={page * limit >= totalAlerts} className="h-[32px] px-3 bg-surface border border-border rounded-btn text-[13px] text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-border transition-colors flex items-center gap-1" data-id="element-135">
            Next <ChevronRight className="w-4 h-4" data-id="element-136" />
          </button>
        </div>
      </div>

      {/* Toast */}
      {toast && <div className={`fixed top-6 right-6 px-4 py-3 rounded-badge shadow-lg flex items-center gap-2 z-50 animate-in slide-in-from-top-5 ${toast.type === 'success' ? 'bg-success text-white' : 'bg-danger text-white'}`} data-id="element-137">
          {toast.type === 'success' ? <CheckCircle className="w-4 h-4" data-id="element-138" /> : <AlertTriangle className="w-4 h-4" data-id="element-139" />}
          <span className="text-[14px] font-medium" data-id="element-140">{toast.message}</span>
        </div>}

      {/* Resolve All Modal */}
      {isResolveAllOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-141">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-[4px]" onClick={() => setIsResolveAllOpen(false)} data-id="element-142" />
          <div className="relative w-full max-w-[360px] bg-surface border border-border rounded-card p-6 text-center shadow-2xl" data-id="element-143">
            <div className="w-16 h-16 rounded-full bg-[rgba(239,68,68,0.1)] flex items-center justify-center mx-auto mb-4" data-id="element-144">
              <AlertTriangle className="w-8 h-8 text-danger" data-id="element-145" />
            </div>
            <h2 className="text-[16px] font-semibold text-white mb-2" data-id="element-146">
              Resolve all active alerts?
            </h2>
            <p className="text-[14px] text-text-secondary mb-6" data-id="element-147">
              This will mark{' '}
              <strong className="text-white" data-id="element-148">{activeAlertsCount}</strong> active
              alerts as resolved.
            </p>
            <div className="flex gap-3" data-id="element-149">
              <button onClick={() => setIsResolveAllOpen(false)} className="flex-1 h-[40px] bg-surface border border-border text-white rounded-btn font-medium hover:bg-border transition-colors" data-id="element-150">
                Cancel
              </button>
              <button onClick={handleResolveAll} className="flex-1 h-[40px] bg-danger text-white rounded-btn font-medium hover:opacity-90 transition-opacity" data-id="element-151">
                Resolve All
              </button>
            </div>
          </div>
        </div>}

      {/* Detail Panel */}
      <AlertDetailPanel alert={viewAlert} onClose={() => setViewAlert(null)} onResolve={() => {
      if (viewAlert) handleResolve(viewAlert.id);
      setViewAlert(null);
    }} data-id="element-152" />
    </div>;
}
// --- Subcomponents ---
function AlertDetailPanel({
  alert,
  onClose,
  onResolve
}: {
  alert: Alert | null;
  onClose: () => void;
  onResolve: () => void;
}) {
  if (!alert) return null;
  let severityColor = '';
  let severityBg = '';
  if (alert.severity === 'critical' || alert.severity === 'high') {
    severityColor = 'text-danger';
    severityBg = 'bg-[rgba(239,68,68,0.15)]';
  } else if (alert.severity === 'medium') {
    severityColor = 'text-warning';
    severityBg = 'bg-[rgba(245,158,11,0.15)]';
  } else {
    severityColor = 'text-success';
    severityBg = 'bg-[rgba(34,197,94,0.15)]';
  }
  const formatAlertType = (type: string) => {
    return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
  };
  return <div className="fixed inset-0 z-40 flex justify-end" data-id="element-153">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} data-id="element-154" />
      <div className="relative w-[420px] h-full bg-surface border-l border-border shadow-2xl flex flex-col animate-in slide-in-from-right duration-250" data-id="element-155">
        {/* Header */}
        <div className="p-6 border-b border-border relative" data-id="element-156">
          <button onClick={onClose} className="absolute top-6 right-6 text-text-secondary hover:text-white transition-colors" data-id="element-157">
            <X className="w-5 h-5" data-id="element-158" />
          </button>
          <div className="flex items-start gap-4 pr-8" data-id="element-159">
            <div className={`w-10 h-10 rounded-full ${severityBg} flex items-center justify-center flex-shrink-0 mt-1`} data-id="element-160">
              <AlertCircle className={`w-5 h-5 ${severityColor}`} data-id="element-161" />
            </div>
            <div data-id="element-162">
              <div className="flex items-center gap-2 mb-1" data-id="element-163">
                <span className={`px-2 py-0.5 rounded-badge ${severityBg} ${severityColor} text-[10px] uppercase tracking-wider font-bold`} data-id="element-164">
                  {alert.severity}
                </span>
                {alert.status === 'active' ? <span className="px-2 py-0.5 rounded-badge bg-[rgba(239,68,68,0.1)] text-danger text-[10px] uppercase tracking-wider font-bold" data-id="element-165">
                    Active
                  </span> : <span className="px-2 py-0.5 rounded-badge bg-[rgba(34,197,94,0.1)] text-success text-[10px] uppercase tracking-wider font-bold" data-id="element-166">
                    Resolved
                  </span>}
              </div>
              <h2 className="text-[18px] font-semibold text-white" data-id="element-167">
                {formatAlertType(alert.alertType)}
              </h2>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-8" data-id="element-168">
          {/* Message */}
          <div data-id="element-169">
            <h3 className="text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] mb-2" data-id="element-170">
              Message
            </h3>
            <div className="bg-background border border-border rounded-card p-4" data-id="element-171">
              <p className="text-[14px] text-white leading-relaxed" data-id="element-172">
                {alert.message}
              </p>
            </div>
          </div>

          {/* Timing */}
          <div className="space-y-4" data-id="element-173">
            <h3 className="text-[12px] uppercase font-semibold text-text-secondary tracking-[1px]" data-id="element-174">
              Timing
            </h3>
            <div className="space-y-3" data-id="element-175">
              <div className="flex justify-between" data-id="element-176">
                <span className="text-[13px] text-text-secondary" data-id="element-177">
                  Triggered At
                </span>
                <span className="text-[13px] text-white" data-id="element-178">
                  {new Date(alert.triggeredAt).toLocaleString()}
                </span>
              </div>
              {alert.resolvedAt && <div className="flex justify-between" data-id="element-179">
                  <span className="text-[13px] text-text-secondary" data-id="element-180">
                    Resolved At
                  </span>
                  <span className="text-[13px] text-white" data-id="element-181">
                    {new Date(alert.resolvedAt).toLocaleString()}
                  </span>
                </div>}
            </div>
          </div>

          {/* Entities */}
          <div className="space-y-4" data-id="element-182">
            <h3 className="text-[12px] uppercase font-semibold text-text-secondary tracking-[1px]" data-id="element-183">
              Related Entities
            </h3>

            {alert.user && <div className="bg-background border border-border rounded-card p-3 flex justify-between items-center" data-id="element-184">
                <div data-id="element-185">
                  <p className="text-[11px] text-text-secondary mb-1" data-id="element-186">User</p>
                  <p className="text-[13px] text-white" data-id="element-187">
                    {alert.user.fullName}
                  </p>
                  <p className="text-[11px] text-text-tertiary" data-id="element-188">
                    {alert.user.email}
                  </p>
                </div>
                <button className="text-[12px] text-accent hover:underline" data-id="element-189">
                  View
                </button>
              </div>}

            {alert.device && <div className="bg-background border border-border rounded-card p-3 flex justify-between items-center" data-id="element-190">
                <div data-id="element-191">
                  <p className="text-[11px] text-text-secondary mb-1" data-id="element-192">Device</p>
                  <p className="text-[13px] text-white font-mono" data-id="element-193">
                    {alert.device.deviceUid}
                  </p>
                </div>
                <button className="text-[12px] text-accent hover:underline" data-id="element-194">
                  View
                </button>
              </div>}

            {alert.sessionId && <div className="bg-background border border-border rounded-card p-3 flex justify-between items-center" data-id="element-195">
                <div data-id="element-196">
                  <p className="text-[11px] text-text-secondary mb-1" data-id="element-197">
                    Session
                  </p>
                  <p className="text-[13px] text-white font-mono" data-id="element-198">
                    {alert.sessionId.substring(0, 8)}...
                  </p>
                </div>
                <button className="text-[12px] text-accent hover:underline" data-id="element-199">
                  View Session
                </button>
              </div>}
          </div>
        </div>

        {/* Footer Actions */}
        {alert.status === 'active' && <div className="p-6 border-t border-border" data-id="element-200">
            <button onClick={onResolve} className="w-full h-[40px] bg-[rgba(34,197,94,0.1)] text-success border border-[rgba(34,197,94,0.2)] rounded-btn font-medium hover:bg-[rgba(34,197,94,0.2)] transition-colors flex items-center justify-center gap-2" data-id="element-201">
              <CheckCircle className="w-4 h-4" data-id="element-202" />
              Mark as Resolved
            </button>
          </div>}
      </div>
    </div>;
}